/**
 * Constants for roll-media (image / video) capture and upload (TICKET-281c, TICKET-290).
 * @module constants/rollMedia
 */

/** Hard cap on motion-driven recording duration in seconds (TICKET-290). */
export const HARD_CAP_RECORDING_SECONDS = 15;

/** No-motion timeout while waiting to detect a roll (TICKET-290). */
export const NO_MOTION_TIMEOUT_MS = 10_000;

/**
 * Default video bitrate hint (500 kbps) for MediaRecorder.
 *
 * Note: many Android Chrome implementations of VP9 ignore this hint and
 * produce ~10x the requested rate. We pair this with the H.264-first MIME
 * preference below — H.264 implementations typically honour the bitrate
 * target much more reliably.
 */
export const ROLL_VIDEO_BITRATE_BPS = 500_000;

/** Maximum allowed video size pre-base64 (4 MB). Oversize blobs are rejected. */
export const MAX_ROLL_VIDEO_BYTES = 4 * 1024 * 1024;

/**
 * Ordered list of MIME types tried by MediaRecorder. First supported wins.
 *
 * H.264 (mp4) is preferred over VP9/VP8 because Android Chrome's VP9
 * encoder ignores `videoBitsPerSecond` (real-world: ~13 Mbps for a 1 Mbps
 * hint at 1280x960). H.264 honours the hint reliably across browsers.
 * VP8 is the next-best fallback; VP9 is last.
 */
export const ROLL_VIDEO_MIME_CANDIDATES = [
  'video/mp4;codecs=avc1',
  'video/mp4',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/webm;codecs=vp9',
] as const;

/** Narrowed content-type sent to the upload API. */
export type RollVideoContentType = 'video/webm' | 'video/mp4';
